import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CreateCompoundRecordVersionDocument,
  type CreateCompoundRecordVersionMutation,
  type CreateCompoundRecordVersionMutationVariables,
} from "~/features/compoundRecordVersions/gqls";
import type { CompoundRecordVersionFormData } from "~/features/compoundRecordVersions/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseCompoundRecordVersionCreateOptions {
  onSuccess?: () => void;
}

/** Create a compound-record version. Refetches the list on success. */
export function useCompoundRecordVersionCreate({ onSuccess }: UseCompoundRecordVersionCreateOptions = {}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [mutate] = useMutation<CreateCompoundRecordVersionMutation, CreateCompoundRecordVersionMutationVariables>(
    CreateCompoundRecordVersionDocument,
    { refetchQueries: ["GetCompoundRecordVersionsList"] },
  );

  const submit = useCallback(
    async (form: CompoundRecordVersionFormData) => {
      if (!tenantId) return;
      setProcessing(true);
      try {
        const res = await mutate({ variables: { input: { tenantId, version: form.version } } });
        if (res.data?.cnutCreateCompoundRecordVersion.__typename === "CompoundRecordVersionCreateOutput") {
          toast.success(t("feature.compoundRecordVersions.create.success"));
          onSuccess?.();
        } else {
          toast.error(t("feature.compoundRecordVersions.create.error"));
        }
      } catch (e) {
        toast.error(translateError(t, e, t("feature.compoundRecordVersions.create.error")));
      } finally {
        setProcessing(false);
      }
    },
    [mutate, tenantId, t, onSuccess],
  );

  return { submit, processing };
}
